import { Typography, useTheme, Box } from '@mui/material';
import { type GridColumns, type GridValidRowModel } from '@mui/x-data-grid';
import { CustomDataGrid } from 'components';
import { colorTheme } from 'theme';
import { getAllUserPlanRequests } from 'service';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { strings } from 'i18n/Strings';
import { commonConstantTexts } from 'constants/constantText';
import { useSelector } from 'react-redux';

interface PlanRequests {
  requests: Request[];
}

interface Request {
  requestId: string;
  requestedRetirementPlanId: string;
  requestedPensionPlanId: string;
  status: string;
  user: User;
  requestedRetirementPlan: { planName: string };
  currentRetirementPlan: { planName: string };
  requestedPensionPlan: { name: string };
  currentPensionPlan: { name: string };
}

interface User {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  callingCode: string;
  phone: string;
}

const UserPlanChangeRequests = (): JSX.Element => {
  const theme = useTheme();
  const colors = colorTheme(theme.palette.mode);
  const { id } = useParams();
  const [planData, setPlanData] = useState<Request[]>([]);
  const [pageCount, setPageCount] = useState(null);
  const pageInfo = useSelector(
    (state) => state?.pagination?.planChangeRequests,
  );
  const getUserPlanRequests = async (): Promise<void> => {
    try {
      const userPlanRequests = (await getAllUserPlanRequests(
        id as string,
        Number(pageInfo?.page) + 1,
        pageInfo?.pageSize,
      )) as PlanRequests;
      if (userPlanRequests?.requests) {
        setPlanData(userPlanRequests?.requests?.rows);
        setPageCount(userPlanRequests?.requests?.count);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    void getUserPlanRequests();
  }, [pageInfo?.page, pageInfo?.pageSize]);

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: 'id',
      flex: 1,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.requestId}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.requestId}
        </Typography>
      ),
    },
    {
      field: 'existingPensionPlan',
      flex: 1,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.existingPensionPlan}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.currentPensionPlan.name}
        </Typography>
      ),
    },
    {
      field: 'existingRetirementPlan',
      flex: 1.5,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.existingRetirementOption}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.currentRetirementPlan.planName}
        </Typography>
      ),
    },
    {
      field: 'newPensionPlan',
      flex: 1,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.newPensionPlan}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.requestedPensionPlan.name}
        </Typography>
      ),
    },
    {
      field: 'newRetirementPlan',
      flex: 1.3,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.newRetirementOption}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.requestedRetirementPlan.planName}
        </Typography>
      ),
    },
    {
      field: 'status',
      flex: 1,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.statusColumn}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.status}
        </Typography>
      ),
    },
  ];

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}></Box>
      <CustomDataGrid
        hideActionsCondition={(row) => row.status !== 'Pending'}
        columns={columns}
        rows={planData}
        getRowId={(row: Request) => `${String(row.requestId)}`}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0
            ? commonConstantTexts.evenText
            : commonConstantTexts.oddText
        }
        currentPage={commonConstantTexts.planChangeRequestsPage}
        pageCount={pageCount}
      />
    </>
  );
};

export default UserPlanChangeRequests;
